import type { ReactNode } from 'react';

import { OrderStatusColors, toDisplayStatusKey } from './model/orderCell';
import { getStatus, getStatusTitle } from './orderCellService';
import { Status } from './Status';

import dayjs from 'dayjs';

import localizedFormat from 'dayjs/plugin/localizedFormat';

import { Typography } from '@/components/ui/typography';

/**
 * Data shape for OrderCell.
 * Callers (e.g. propertyListTransform) pass generic Record<string, unknown>.
 */
type OrderCellData = Record<string, unknown>;

dayjs.extend(localizedFormat);

/** Orders-table "Order Status" cell: status title, last-updated line and
    progress dots. `badge` is an optional status marker rendered in the cell's
    top-right corner, beside the title block. */
export const OrderCell = ({
  data,
  badge
}: {
  data: OrderCellData;
  badge?: ReactNode;
}) => {
  const orderStatusValue = data.orderStatus as string;
  const orderStatusUpdatedAt = data.orderStatusUpdatedAt as
    | number
    | null
    | undefined;

  const statusKey = toDisplayStatusKey(orderStatusValue);
  const orderStatus = getStatus(statusKey) ?? null;

  const getDate = (dateInput: number | null | undefined) => {
    if (dateInput == null) {
      return '-';
    } else {
      const getLastSyncTime = dayjs(dateInput).format('LL');
      return `Status updated on ${getLastSyncTime}`;
    }
  };

  const getColor = (key: boolean | null | undefined): string => {
    if (key === null) {
      return OrderStatusColors.RED;
    } else if (key === true) {
      return OrderStatusColors.GREEN;
    } else if (key === false) {
      return OrderStatusColors.GRAY;
    } else if (key === undefined) {
      return OrderStatusColors.YELLOW;
    }
    return OrderStatusColors.GRAY;
  };

  return (
    <section className="w-full space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 space-y-0.5">
          <Typography as="div" variant="body-medium">
            {getStatusTitle(statusKey)}
          </Typography>
          <Typography
            as="div"
            variant="small-medium"
            className="text-muted-foreground"
          >
            {getDate(orderStatusUpdatedAt)}
          </Typography>
        </div>
        {badge}
      </div>
      <div
        className="flex items-center gap-[5px]"
        data-testid="order-status-progress"
      >
        {orderStatus &&
          Object.keys(orderStatus).map((key: string) => {
            return (
              <Status
                color={getColor(
                  (orderStatus as Record<string, boolean | null | undefined>)[
                    key
                  ]
                )}
                key={key}
              />
            );
          })}
      </div>
    </section>
  );
};
